import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PhonePanel, TrustedDevicesPanel } from "@/components/SignInMethods";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { jmd } from "@/lib/format";
import { NATIONALITIES } from "@/lib/countries";
import { IdentityFields } from "@/components/IdentityFields";
import { toast } from "sonner";
import { DriverMap } from "@/components/DriverMap";
import { PushToggle } from "@/components/PushToggle";
import { DisputeButton } from "@/components/DisputeForm";
import {
  Package, Heart, MapPin, Wallet, Star, Hotel, LayoutGrid, Loader2, Check, Truck, Box, UserCog, KeyRound,
} from "lucide-react";
import { MfaPanel } from "@/components/MfaPanel";

const STAGES = [
  { k: "confirmed", l: "Confirmed", icon: Check },
  { k: "packed", l: "Packed", icon: Box },
  { k: "dispatched", l: "Dispatched", icon: Package },
  { k: "out_for_delivery", l: "Out for Delivery", icon: Truck },
  { k: "delivered", l: "Delivered", icon: Check },
];

function OrderTracker({ order }) {
  const idx = Math.max(...order.stages.map((s) => STAGES.findIndex((x) => x.k === s)), 0);
  return (
    <div className="bg-card rounded-2xl border border-border p-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><div className="font-mono-sn font-semibold">{order.order_no}</div><div className="text-xs text-muted-foreground">{order.items.length} item(s) · {jmd(order.totals.total)} · {order.payment_status}</div></div>
        <span className="text-xs font-semibold bg-emerald/10 text-emerald px-2.5 py-1 rounded-full capitalize">{order.status.replace(/_/g, " ")}</span>
      </div>
      <div className="flex items-center mt-4">
        {STAGES.map((s, i) => (
          <React.Fragment key={s.k}>
            <div className="flex flex-col items-center gap-1">
              <span className={`h-8 w-8 grid place-items-center rounded-full ${i <= idx ? "bg-emerald text-white" : "bg-secondary text-muted-foreground"}`}><s.icon size={15} /></span>
              <span className={`text-[10px] ${i <= idx ? "text-ink font-semibold" : "text-muted-foreground"} hidden sm:block`}>{s.l}</span>
            </div>
            {i < STAGES.length - 1 && <div className={`h-0.5 flex-1 ${i < idx ? "bg-emerald" : "bg-border"}`} />}
          </React.Fragment>
        ))}
      </div>
      {(order.status === "out_for_delivery" || order.status === "delivered") && (
        <div className="mt-4"><DriverMap order={order} /></div>
      )}
    </div>
  );
}

const TABS = [
  { k: "overview", l: "Overview", icon: LayoutGrid },
  { k: "orders", l: "Orders", icon: Package },
  { k: "bookings", l: "Bookings", icon: Hotel },
  { k: "wishlist", l: "Wishlist", icon: Heart },
  { k: "rewards", l: "SHOP Wallet", icon: Wallet },
  { k: "profile", l: "Profile", icon: UserCog },
];

function ProfilePanel({ user }) {
  const [f, setF] = useState({ name: user.name || "", sex: user.sex || "", nationality: user.nationality || "", dob: user.dob || "", parish: user.parish || "", id_type: user.id_type || "", id_number: user.id_number || "", criminal_declared: user.criminal_declaration?.declared ?? null, criminal_details: user.criminal_declaration?.details || "", attest: !!user.criminal_declaration?.attested_at, marketing_opt_in: !!user.marketing_opt_in, deal_reminder_opt_in: !!user.deal_reminder_opt_in });
  const gap = user.identity_gap || [];
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name cannot be empty"); return; }
    setSaving(true);
    try { await api.patch("/auth/me", f); toast.success("Profile updated"); }
    catch { toast.error("Could not update profile"); }
    setSaving(false);
  };
  return (
    <div className="bg-card rounded-2xl border border-border p-6 max-w-lg" data-testid="profile-panel">
      <h3 className="font-display font-bold text-lg mb-4">Your Profile</h3>
      <div className="space-y-3">
        <label className="block text-xs font-semibold text-muted-foreground">Full name
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full rounded-xl border border-border px-4 py-3 text-sm mt-1 font-normal text-ink" data-testid="profile-name" />
        </label>
        <label className="block text-xs font-semibold text-muted-foreground">Email
          <input value={user.email} disabled className="w-full rounded-xl border border-border px-4 py-3 text-sm mt-1 font-normal text-muted-foreground bg-secondary" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs font-semibold text-muted-foreground">Sex
            <select value={f.sex} onChange={(e) => setF({ ...f, sex: e.target.value })} className="w-full rounded-xl border border-border px-4 py-3 text-sm mt-1 font-normal text-ink bg-white" data-testid="profile-sex">
              <option value="">Prefer not to say</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </label>
          <label className="block text-xs font-semibold text-muted-foreground">Nationality
            <select value={f.nationality} onChange={(e) => setF({ ...f, nationality: e.target.value })} className="w-full rounded-xl border border-border px-4 py-3 text-sm mt-1 font-normal text-ink bg-white" data-testid="profile-nationality">
              <option value="">Select…</option>
              {NATIONALITIES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>
        <div className="pt-2">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Identity & declaration</div>
          {gap.length > 0 && <div className="text-xs bg-gold/10 rounded-lg px-3 py-2 mb-2" data-testid="identity-gap">Required before you can be approved as a driver, dealer, merchant or supplier: {gap.map((g) => g.replace(/_/g, " ")).join(", ")}</div>}
          <IdentityFields f={f} setF={setF} full testPrefix="profile" />
        </div>

        <label className="flex items-start gap-2.5 text-sm rounded-xl border border-border p-3 cursor-pointer" data-testid="marketing-opt-in">
          <input type="checkbox" checked={f.marketing_opt_in} onChange={(e) => setF({ ...f, marketing_opt_in: e.target.checked })} className="mt-0.5" data-testid="marketing-opt-in-checkbox" />
          <span><span className="font-semibold text-ink">Email me New This Week</span><span className="block text-xs text-muted-foreground mt-0.5">Get the freshest arrivals and member deals. SHOP+ members are always included.</span></span>
        </label>
        <label className="flex items-start gap-2.5 text-sm rounded-xl border border-border p-3 cursor-pointer" data-testid="deal-reminder-opt-in">
          <input type="checkbox" checked={f.deal_reminder_opt_in} onChange={(e) => setF({ ...f, deal_reminder_opt_in: e.target.checked })} className="mt-0.5" data-testid="deal-reminder-opt-in-checkbox" />
          <span><span className="font-semibold text-ink">Deal of the Day reminder</span><span className="block text-xs text-muted-foreground mt-0.5">A short morning email the moment each new daily deal goes live.</span></span>
        </label>
        <button onClick={save} disabled={saving} className="bg-ink text-white rounded-xl px-6 py-3 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="profile-save-btn">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save Changes
        </button>
      </div>
      <ChangePasswordPanel />
      <MfaPanel />
      <PasskeysPanel />
      <PhonePanel />
      <TrustedDevicesPanel />
    </div>
  );
}

function ChangePasswordPanel() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password updated — other sessions were signed out");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Could not change password"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-card rounded-2xl border border-border p-6 space-y-3" data-testid="change-password-panel">
      <h3 className="font-semibold flex items-center gap-2"><KeyRound size={16} /> Change password</h3>
      <input type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} placeholder="Current password" autoComplete="current-password" data-testid="current-password-input" className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" />
      <div className="grid sm:grid-cols-2 gap-3">
        <input type="password" required minLength={8} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} placeholder="New password (8+ characters)" autoComplete="new-password" data-testid="new-password-input" className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" />
        <input type="password" required value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} placeholder="Confirm new password" autoComplete="new-password" data-testid="confirm-password-input" className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" />
      </div>
      <button disabled={busy} data-testid="change-password-submit" className="bg-ink text-white rounded-xl px-6 py-2.5 font-semibold text-sm flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Update password</button>
    </form>
  );
}

function PasskeysPanel() {
  const [creds, setCreds] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/webauthn/credentials").then(({ data }) => setCreds(data)).catch(() => setCreds([]));
  useEffect(() => { load(); }, []);
  const addPasskey = async () => {
    setBusy(true);
    try {
      const { startRegistration } = await import("@simplewebauthn/browser");
      const { data: opts } = await api.post("/webauthn/register/options");
      const attResp = await startRegistration({ optionsJSON: opts });
      await api.post("/webauthn/register/verify", attResp);
      toast.success("Passkey added"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add passkey (cancelled or unsupported)"); }
    setBusy(false);
  };
  const remove = async (id) => {
    try { await api.delete(`/webauthn/credentials/${id}`); await load(); toast.success("Passkey removed"); }
    catch { toast.error("Could not remove"); }
  };
  return (
    <div className="mt-6 pt-6 border-t border-border" data-testid="passkeys-panel">
      <h3 className="font-display font-bold text-lg mb-1">Face ID, fingerprint & passkeys</h3>
      <p className="text-xs text-muted-foreground mb-4">Sign in with Face ID, Touch ID, fingerprint, Windows Hello or a security key — no password to type or steal. Your biometrics stay on your device; SHOP NOW only stores a secure key. You can combine this with 2FA for staff accounts.</p>
      <Link to="/account/biometrics" className="inline-flex items-center gap-2 mb-4 rounded-xl bg-emerald text-white px-5 py-2.5 text-sm font-semibold" data-testid="bio-setup-link">Guided set-up: scan face / fingerprint →</Link>
      {(creds || []).map((c) => (
        <div key={c.id} className="flex items-center justify-between bg-secondary rounded-xl px-4 py-2.5 mb-2" data-testid={`passkey-${c.id}`}>
          <span className="text-sm">Passkey · added {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</span>
          <button onClick={() => remove(c.id)} className="text-xs font-semibold text-coral" data-testid={`passkey-remove-${c.id}`}>Remove</button>
        </div>
      ))}
      <button onClick={addPasskey} disabled={busy} className="mt-2 bg-ink text-white rounded-xl px-6 py-3 font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="passkey-add-btn">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Turn on Face ID / fingerprint sign-in
      </button>
    </div>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const { wishlist } = useCart();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);

  useEffect(() => { if (user === false) navigate("/login"); }, [user]);
  useEffect(() => {
    if (!user) return;
    api.get("/my/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/my/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/my/bookings").then(({ data }) => setBookings(data)).catch(() => {});
  }, [user]);

  if (!user) return <div className="grid place-items-center py-40"><Loader2 className="animate-spin text-emerald" size={32} /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-3xl font-bold flex items-center gap-2">Hi, {user.name.split(" ")[0]} 👋 {user.shop_plus && <span className="inline-flex items-center gap-1 text-xs bg-gold text-ink font-bold px-2.5 py-1 rounded-full">SHOP+</span>}</h1><p className="text-muted-foreground">Manage your orders, bookings and rewards.</p></div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1">
          <div className="bg-card rounded-2xl border border-border p-2 sticky top-28" data-testid="dashboard-nav-tabs">
            {TABS.map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === t.k ? "bg-ink text-white" : "hover:bg-secondary"}`} data-testid={`tab-${t.k}`}>
                <t.icon size={17} /> {t.l}
              </button>
            ))}
            <button onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-coral hover:bg-secondary">Sign out</button>
          </div>
        </aside>

        <div className="lg:col-span-3 space-y-4">
          {tab === "overview" && summary && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[["Orders", summary.orders, Package], ["Spent", jmd(summary.spent), Wallet], ["Bookings", summary.bookings, Hotel], ["SHOP Points", summary.points.toLocaleString(), Star]].map(([l, v, Ic]) => (
                  <div key={l} className="bg-card rounded-2xl border border-border p-5"><Ic size={20} className="text-emerald mb-2" /><div className="font-display font-bold text-2xl">{v}</div><div className="text-xs text-muted-foreground">{l}</div></div>
                ))}
              </div>
              <PushToggle />
              <div className="bg-card rounded-2xl border border-border p-6">
                <h3 className="font-display font-bold mb-3">Recent Orders</h3>
                {orders.length === 0 ? <p className="text-muted-foreground text-sm">No orders yet. <Link to="/" className="text-emerald font-semibold">Start shopping →</Link></p> : orders.slice(0, 2).map((o) => <div key={o.id} className="mb-3"><OrderTracker order={o} /></div>)}
              </div>
            </>
          )}

          {tab === "orders" && (
            <div className="space-y-4">
              {orders.length === 0 ? <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No orders yet.</div> : orders.map((o) => <div key={o.id}><OrderTracker order={o} /><DisputeButton order={o} onOpened={(d) => setOrders(orders.map((x) => (x.order_no === d.order_no ? { ...x, dispute_ref: d.ref, dispute_status: d.status } : x)))} /></div>)}
            </div>
          )}

          {tab === "bookings" && (
            <div className="space-y-3">
              {bookings.length === 0 ? <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No bookings yet. <Link to="/hotels" className="text-emerald font-semibold">Find a stay →</Link></div> : bookings.map((b) => (
                <div key={b.id} className="bg-card rounded-2xl border border-border p-5 flex items-center justify-between">
                  <div><div className="font-display font-semibold">{b.room_name}</div><div className="text-sm text-muted-foreground">{b.check_in} → {b.check_out} · {b.guests} guests · {b.nights} night(s)</div><div className="font-mono-sn text-xs mt-1">{b.ref}</div></div>
                  <div className="font-display font-bold">{jmd(b.total)}</div>
                </div>
              ))}
            </div>
          )}

          {tab === "wishlist" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {wishlist.length === 0 ? <div className="col-span-full bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">Your wishlist is empty.</div> : wishlist.map((w) => (
                <Link to={`/product/${w.id}`} key={w.id} className="bg-card rounded-2xl border border-border overflow-hidden sn-card-hover">
                  <img src={w.image} alt={w.name} className="aspect-square w-full object-cover" />
                  <div className="p-3"><div className="text-sm font-medium line-clamp-2">{w.name}</div><div className="font-display font-bold mt-1">{jmd(w.price)}</div></div>
                </Link>
              ))}
            </div>
          )}

          {tab === "rewards" && summary && (
            <div className="space-y-4">
              <div className="rounded-2xl sn-hero-mesh sn-noise p-8 text-white relative overflow-hidden">
                <div className="text-sm text-white/60 uppercase tracking-wider">SHOP Wallet</div>
                <div className="font-clash font-bold text-5xl mt-2">{summary.points.toLocaleString()}</div>
                <div className="text-white/70 mt-1">SHOP Points · worth {jmd(summary.points)}</div>
                <p className="text-white/60 text-sm mt-4 max-w-md">Earn 2% back in SHOP Points on every order. Redeem up to J$10,000 per checkout. Sign up for SHOP+ soon for even more rewards.</p>
              </div>
              <div className="bg-card rounded-2xl border border-border p-6">
                <h3 className="font-display font-bold mb-2">How SHOP Points work</h3>
                <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-5">
                  <li>Earn 2% back on every completed purchase</li>
                  <li>1 point = J$1 towards future orders</li>
                  <li>Redeem at checkout with the SHOP Points slider</li>
                  <li>New members start with 500 bonus points</li>
                </ul>
              </div>
            </div>
          )}
          {tab === "profile" && <ProfilePanel user={user} />}
        </div>
      </div>
    </div>
  );
}
